<template>
    <div class="box is-hoverable is-condensed has-no-glow-effect setting-box">
        <p class="setting-box__description">{{ translatedDescription }}</p>

        <template v-if="editMode">
            <div class="setting-box__controls">
                <div class="select is-small" v-if="eventSetting.type === 'bool'">
                    <select v-model="editValue">
                        <option :value="true">{{ translate("event_setting_type.bool.true") }}</option>
                        <option :value="false">{{ translate("event_setting_type.bool.false") }}</option>
                    </select>
                </div>

                <input class="input is-small" v-if="eventSetting.type === 'int'" v-model="editValue" type="number" min="0" />

                <div class="buttons mb-0">
                    <LoadingButton :is-loading="savingInProgress" class="button is-small is-success" @click="confirmEdit">
                        <span class="icon is-small"><i class="fas fa-check" /></span>
                        <span>{{ translate("common.ok") }}</span>
                    </LoadingButton>
                    <button class="button is-small is-danger" @click="editMode = false" :disabled="savingInProgress">
                        {{ translate("common.cancel") }}
                    </button>
                </div>
            </div>
        </template>

        <template v-else>
            <div class="setting-box__controls">
                <span
                    class="tag is-medium"
                    :class="eventSetting.type === 'bool' ? (eventSetting.value ? 'is-success' : 'is-dark') : 'is-info'"
                >
                    {{ prettyValue }}
                </span>

                <div class="buttons mb-0">
                    <button class="button is-small is-primary" @click="startEdit" :disabled="savingInProgress">
                        <span class="icon is-small"><i class="fas fa-edit" /></span>
                        <span>{{ translate("common.edit") }}</span>
                    </button>
                    <LoadingButton :is-loading="savingInProgress" class="button is-small" @click="resetSetting">
                        <span class="icon is-small"><i class="fas fa-sync" /></span>
                        <span>{{ translate("common.reset") }}</span>
                    </LoadingButton>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import eventSettingsApi, { SingleEventSetting } from "../../Api/eventSettingsApi";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import { successToast } from "../../Common/toasts";

    interface Props {
        eventId: number;
    }

    const props = defineProps<Props>();
    const eventSetting = defineModel<SingleEventSetting>({ required: true });

    const editMode = ref(false);
    const editValue = ref<boolean | number | null>(null);
    const savingInProgress = ref(false);

    const prettyValue = computed(() => {
        if (eventSetting.value.type === "bool") {
            return !!eventSetting.value.value ? translate("event_setting_type.bool.true") : translate("event_setting_type.bool.false");
        }
        if (eventSetting.value.type === "int") {
            return ~~eventSetting.value.value;
        }

        throw Error("Unsupported type.");
    });

    const translatedDescription = computed(() => {
        return translate("event_setting." + eventSetting.value.name);
    });

    const resetSetting = (): Promise<void> => {
        savingInProgress.value = true;
        return eventSettingsApi
            .resetSetting(props.eventId, eventSetting.value.name)
            .then(resetResponse => {
                eventSetting.value.value = resetResponse.defaultValue;
                triggerInputUpdate(resetResponse.defaultValue);
                successToast(translate("common.changes_have_been_saved"));
            })
            .catch(handleError)
            .finally(() => {
                savingInProgress.value = false;
            });
    };

    const startEdit = (): void => {
        editMode.value = true;
        editValue.value = eventSetting.value.value;
    };

    const confirmEdit = (): Promise<void> => {
        savingInProgress.value = true;
        return eventSettingsApi
            .updateSetting(props.eventId, eventSetting.value.name, editValue.value as boolean | number)
            .then(() => {
                eventSetting.value.value = editValue.value as boolean | number;
                triggerInputUpdate(editValue.value as boolean | number);
                successToast(translate("common.changes_have_been_saved"));
            })
            .catch(handleError)
            .finally(() => {
                editMode.value = false;
                savingInProgress.value = false;
            });
    };

    const triggerInputUpdate = (newValue: boolean | number): void => {
        eventSetting.value = {
            value: newValue,
            type: eventSetting.value.type,
            name: eventSetting.value.name,
        };
    };
</script>

<style scoped>
    .setting-box {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem 1rem;
    }

    .setting-box__description {
        flex: 1 1 16rem;
        margin-bottom: 0;
    }

    .setting-box__controls {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex-wrap: wrap;
    }

    .setting-box__controls .buttons .button {
        min-width: 6.5rem;
        justify-content: center;
    }
</style>
